<template>
  <standard-dialog
    v-model="model"
    :title="restaurantTitle"
    :subtitle="$t('contracts_title')"
    icon="mdi-file-document-multiple-outline"
    :max-width="700"
    hide-actions
  >
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else>
      <v-alert v-if="!canEdit" type="info" text dense class="mb-4">
        {{ $t("services_read_only_notice") }}
      </v-alert>

      <v-btn
        v-if="canEdit"
        color="primary"
        depressed
        class="mb-4"
        @click="openNewContract"
      >
        <v-icon left small>mdi-plus</v-icon>{{ $t("new_contract") }}
      </v-btn>

      <div v-if="!contracts.length" class="text-center grey--text py-6">
        {{ $t("no_contracts") }}
      </div>

      <v-card
        v-for="(contract, index) in contracts"
        :key="contract.id"
        outlined
        class="mb-2 pa-3 contract-row"
        :style="{ borderLeft: '4px solid ' + statusHexColor(contract.status) }"
      >
        <v-row no-gutters align="center">
          <div>
            <span class="font-weight-medium">{{ contract.contract_number }}</span>
            <v-chip
              x-small
              class="ml-2"
              :color="statusColor(contract.status)"
              text-color="white"
            >
              {{ statusLabel(contract) }}
            </v-chip>
            <div class="caption grey--text">
              {{ localeDate(contract.contract_date) }} ·
              {{ localeDate(contract.valid_from) }} –
              {{
                contract.valid_to
                  ? localeDate(contract.valid_to)
                  : $t("contract_no_end_date_short")
              }}
            </div>
            <!-- "i dalje se obračunava" samo za najnoviji ugovor: stariji su zamijenjeni obnovom (lista je sortirana, najnoviji prvi), po njima se računa samo do datuma zatvaranja -->
            <div class="caption grey--text">
              {{ $t("contract_items", { count: contract.broj_usluga }) }}
              <span
                v-if="contract.status === 'ISTEKAO' && index === 0"
                class="warning--text font-weight-medium"
              >
                · {{ $t("still_being_billed") }}
              </span>
            </div>
            <div class="caption grey--text">{{ $t("created_by") }}: {{ contract.created_by || "—" }}</div>
            <div class="caption grey--text" v-if="contract.note">{{ contract.note }}</div>
          </div>
          <v-spacer />
          <a
            v-if="contract.document_url"
            :href="contract.document_url"
            target="_blank"
            class="mr-2"
          >
            <v-icon small>mdi-paperclip</v-icon>
          </a>
          <circle-button
            v-if="canEdit"
            icon="mdi-pencil"
            @click="openEditContract(contract)"
          />
        </v-row>
      </v-card>

      <v-row no-gutters justify="end" class="mt-4">
        <v-btn text @click="close">{{ $t("cancel") }}</v-btn>
      </v-row>
    </template>

    <contract-form-dialog
      v-model="newContractDialog"
      :restaurant="restaurant"
      @saved="fetchContracts"
    />
    <contract-edit-dialog
      v-model="editContractDialog"
      :restaurant-id="restaurant.id"
      :contract="selectedContract"
      @saved="fetchContracts"
    />
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import ContractFormDialog from "./ContractFormDialog.vue";
import ContractEditDialog from "./ContractEditDialog.vue";
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import contractsService from "@/services/contracts";
import {
  contractStatusColor,
  contractStatusHex,
  contractStatusLabel,
} from "@/utils/contractStatus";

export default {
  name: "RestaurantContractsDialog",
  mixins: [hasModel, hasSnackbar],
  components: {
    StandardDialog,
    ContractFormDialog,
    ContractEditDialog,
  },
  props: {
    restaurant: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      canEdit: true,
      contracts: [],
      newContractDialog: false,
      editContractDialog: false,
      selectedContract: {},
    };
  },
  watch: {
    model(val) {
      if (val) {
        this.fetchContracts();
      } else {
        this.contracts = [];
      }
    },
  },
  computed: {
    restaurantTitle() {
      return this.restaurant?.name || "";
    },
  },
  methods: {
    async fetchContracts() {
      this.loading = true;
      try {
        const { data } = await contractsService.getRestaurantContracts(
          this.restaurant.id
        );
        this.canEdit = data.can_edit !== false;
        this.contracts = data.contracts || [];
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_contracts"));
        this.model = false;
      } finally {
        this.loading = false;
      }
    },
    statusColor(status) {
      return contractStatusColor(status);
    },
    statusHexColor(status) {
      return contractStatusHex(status);
    },
    statusLabel(contract) {
      return contractStatusLabel(contract, this.$t.bind(this));
    },
    openNewContract() {
      this.newContractDialog = true;
    },
    openEditContract(contract) {
      this.selectedContract = contract;
      this.editContractDialog = true;
    },
    close() {
      this.model = false;
    },
  },
};
</script>

<style scoped>
.contract-row {
  border-left-width: 4px !important;
}
</style>
